import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  CloudUpload,
  RefreshCw,
  ShieldCheck,
  User,
  Package,
  ShoppingCart,
  AlertCircle,
} from "lucide-react";
import { api } from "../../services/api";
import { formatUsDate, formatUsDateTime } from "../../utils/timezone";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { channelRows } from "./adminData";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const UNAVAILABLE_CHANNELS = new Set(["facebook_marketplace", "walmart"]);
const defaultChannels = channelRows.filter((channel) =>
  ["ebay", "facebook_marketplace", "walmart"].includes(channel.name)
);

const emptyEbayData = {
  accountInfo: null,
  inventory: {
    items: [],
    total: 0,
  },
  orders: {
    items: [],
    total: 0,
  },
  loading: false,
  error: null,
};

function enrichChannel(channel = {}) {
  const name = (channel.name || "").toLowerCase();
  const unavailable = UNAVAILABLE_CHANNELS.has(name);

  return {
    ...channel,
    name,
    displayName:
      channel.displayName ||
      defaultChannels.find((item) => item.name === name)?.label ||
      name ||
      "Unknown",
    status: unavailable ? "not_available" : (channel.status || "disconnected").toLowerCase(),
    realTimeSync: Boolean(channel.realTimeSync),
    unavailable,
    pricingRule: channel.pricingRule || {
      adjustmentType: "none",
      adjustmentValue: 0,
    },
    stats: {
      activeListings: unavailable ? 0 : channel.stats?.activeListings ?? channel.listings ?? 0,
      orders30d: unavailable ? 0 : channel.stats?.orders30d ?? channel.orders30d ?? 0,
      sales30d: unavailable ? 0 : channel.stats?.sales30d ?? channel.sales30d ?? 0,
      failedJobs: unavailable ? 0 : channel.stats?.failedJobs ?? channel.failedJobs ?? 0,
    },
  };
}

function mergeChannels(rawChannels = []) {
  const channelMap = Object.fromEntries(
    rawChannels
      .filter((channel) => channel?.name)
      .map((channel) => [channel.name.toLowerCase(), channel])
  );

  return defaultChannels.map((defaultChannel) =>
    enrichChannel(channelMap[defaultChannel.name] || defaultChannel)
  );
}

function money(value, currency = "USD") {
  const amount = Number(value || 0);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

function number(value) {
  return Number(value || 0).toLocaleString();
}

function formatDateTime(value) {
  return formatUsDateTime(value);
}

function formatDate(value) {
  return formatUsDate(value);
}

function statusTone(status) {
  if (status === "not_available") return "neutral";
  if (status === "connected") return "good";
  if (status === "syncing") return "info";
  if (status === "error") return "bad";
  return "warn";
}

function getLastSyncDate(channel = {}) {
  if (channel.unavailable) return null;
  return channel.lastInventorySyncAt || channel.lastProductSyncAt || channel.updatedAt || null;
}

function getChannelRecordStats(channel = {}, ebayData = emptyEbayData) {
  if (channel.unavailable) {
    return {
      listings: 0,
      orders30d: 0,
      sales30d: 0,
    };
  }

  if (channel.name === "ebay") {
    return {
      listings: Number(ebayData.inventory.total || channel.stats?.activeListings || 0),
      orders30d: Number(ebayData.orders.total || channel.stats?.orders30d || 0),
      sales30d: Number(channel.stats?.sales30d || 0),
    };
  }

  return {
    listings: Number(channel.stats?.activeListings || 0),
    orders30d: Number(channel.stats?.orders30d || 0),
    sales30d: Number(channel.stats?.sales30d || 0),
  };
}

function firstAvailable(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "") || null;
}

function getNestedSource(payload = {}) {
  return (
    payload?.sellerInfo ||
    payload?.accountInfo ||
    payload?.user ||
    payload?.seller ||
    payload?.data?.sellerInfo ||
    payload?.data?.accountInfo ||
    payload?.data?.user ||
    payload?.data?.seller ||
    payload?.channel?.sellerInfo ||
    payload?.channel?.accountInfo ||
    payload?.data?.channel?.sellerInfo ||
    payload?.data?.channel?.accountInfo ||
    payload?.channel ||
    payload?.data?.channel ||
    payload?.data ||
    payload
  );
}

function normalizeEbayAccountInfo(payload = {}, fallbackChannel = {}) {
  const source = getNestedSource(payload);

  return {
    sellerId:
      firstAvailable(
        source?.sellerId,
        source?.userId,
        source?.sellerLogin,
        source?.username,
        source?.merchantId,
        source?.accountId,
        source?.id,
        source?._id,
        fallbackChannel?._id
      ) || "N/A",

    sellerName:
      firstAvailable(
        source?.sellerName,
        source?.username,
        source?.sellerLogin,
        source?.businessName,
        source?.displayName,
        source?.name,
        fallbackChannel?.displayName
      ) || "eBay Seller",

    accountStatus:
      firstAvailable(
        source?.businessAccountStatus,
        source?.accountStatus,
        source?.status,
        fallbackChannel?.status
      ) || "connected",

    marketplace:
      firstAvailable(
        source?.marketplaceId,
        source?.marketplace,
        source?.site,
        source?.siteId,
        source?.name
      ) || "eBay",

    realTimeSync:
      source?.realTimeSync !== undefined
        ? Boolean(source.realTimeSync)
        : Boolean(fallbackChannel?.realTimeSync),

    lastProductSyncAt:
      firstAvailable(source?.lastProductSyncAt, fallbackChannel?.lastProductSyncAt) || null,

    lastInventorySyncAt:
      firstAvailable(source?.lastInventorySyncAt, fallbackChannel?.lastInventorySyncAt) || null,

    connectedAt:
      firstAvailable(source?.connectedAt, source?.createdAt, fallbackChannel?.createdAt) || null,

    updatedAt:
      firstAvailable(source?.updatedAt, fallbackChannel?.updatedAt) || null,
  };
}

function extractInventoryItems(payload = {}) {
  const source = payload?.inventory || payload?.data?.inventory || payload?.data || payload;

  const items =
    source?.inventoryItems ||
    source?.items ||
    source?.listings ||
    source?.activeListings ||
    source?.offers ||
    source?.data ||
    [];

  return {
    items: Array.isArray(items) ? items : [],
    total:
      source?.total ||
      source?.totalItems ||
      source?.count ||
      source?.pagination?.total ||
      (Array.isArray(items) ? items.length : 0),
    limit: source?.limit || payload?.limit || payload?.data?.limit || null,
    offset: source?.offset || payload?.offset || payload?.data?.offset || 0,
    source: source?.source || payload?.source || payload?.data?.source || "ebay",
  };
}

function normalizeInventoryItem(item = {}) {
  const quantity = firstAvailable(
    item?.availability?.shipToLocationAvailability?.quantity,
    item?.quantity?.value,
    item?.quantity,
    item?.availableQuantity,
    item?.availableQuantity?.value,
    item?.stock,
    item?.inventory,
    0
  );

  const priceValue = firstAvailable(
    item?.pricingSummary?.price?.value,
    item?.price?.value,
    item?.price,
    item?.currentPrice?.value,
    typeof item?.currentPrice === "string" || typeof item?.currentPrice === "number"
      ? item.currentPrice
      : null,
    item?.buyItNowPrice?.value
  );

  const currency = firstAvailable(
    item?.pricingSummary?.price?.currency,
    item?.price?.currency,
    item?.currentPrice?.currency,
    item?.buyItNowPrice?.currency,
    "USD"
  );

  return {
    id:
      firstAvailable(
        item?.listingId,
        item?.itemId,
        item?.offerId,
        item?.listing?.listingId,
        item?.sku,
        item?.inventoryItemGroupKey
      ) || "N/A",

    sku:
      firstAvailable(
        item?.sku,
        item?.SKU,
        item?.merchantSku,
        item?.inventoryItemGroupKey,
        item?.listingId,
        item?.listing?.listingId,
        item?.itemId
      ) || "N/A",

    title:
      firstAvailable(
        item?.title,
        item?.name,
        item?.listingDescription,
        item?.product?.title,
        item?.product?.aspects?.Title?.[0],
        item?.localizedAspects?.Title?.[0]
      ) || "Untitled Listing",

    quantity: Number(quantity || 0),

    price: priceValue ? money(priceValue, currency) : "N/A",

    condition:
      firstAvailable(
        item?.condition,
        item?.conditionDescription,
        item?.product?.condition,
        "N/A"
      ) || "N/A",

    status:
      firstAvailable(
        item?.status,
        item?.listingStatus,
        item?.listing?.listingStatus,
        item?.offerStatus,
        item?.availabilityStatus
      ) || (Number(quantity || 0) > 0 ? "Active" : "Out of Stock"),

    image:
      firstAvailable(
        item?.pictureUrl,
        item?.pictureUrls?.[0],
        item?.imageUrl,
        item?.image?.imageUrl,
        item?.thumbnailImages?.[0]?.imageUrl,
        item?.additionalImages?.[0]?.imageUrl,
        item?.product?.imageUrls?.[0],
        item?.product?.aspects?.PictureURL?.[0],
        item?.pictures?.[0],
        item?.galleryURL
      ) || null,
  };
}

function extractOrders(payload = {}) {
  if (Array.isArray(payload?.orders?.orders)) {
    return {
      items: payload.orders.orders,
      total: payload.orders.total || payload.orders.orders.length,
      limit: payload.orders.limit || null,
      offset: payload.orders.offset || 0,
    };
  }

  if (Array.isArray(payload)) {
    return {
      items: payload,
      total: payload.length,
      limit: null,
      offset: 0,
    };
  }

  if (payload?.orderId && Array.isArray(payload?.lineItems)) {
    return {
      items: [payload],
      total: 1,
      limit: 1,
      offset: 0,
    };
  }

  const source =
    payload?.orders && !Array.isArray(payload.orders)
      ? payload.orders
      : payload?.data?.orders && !Array.isArray(payload.data.orders)
      ? payload.data.orders
      : payload?.data || payload;

  const items = Array.isArray(payload?.orders)
    ? payload.orders
    : Array.isArray(payload?.data?.orders)
    ? payload.data.orders
    : source?.orders || source?.items || source?.data || [];

  return {
    items: Array.isArray(items) ? items : [],
    total:
      payload?.total ||
      payload?.data?.total ||
      source?.total ||
      source?.count ||
      source?.pagination?.total ||
      (Array.isArray(items) ? items.length : 0),
    limit: source?.limit || payload?.limit || payload?.data?.limit || null,
    offset: source?.offset || payload?.offset || payload?.data?.offset || 0,
  };
}

function moneyFromAmount(amount, fallbackCurrency = "USD") {
  if (!amount || amount.value === undefined || amount.value === null || amount.value === "") {
    return "N/A";
  }

  return money(amount.value, amount.currency || fallbackCurrency);
}

function formatSignedMoney(amount, fallbackCurrency = "USD") {
  if (!amount || amount.value === undefined || amount.value === null || amount.value === "") {
    return "N/A";
  }

  const value = Number(amount.value || 0);
  const formatted = money(Math.abs(value), amount.currency || fallbackCurrency);

  return value < 0 ? `-${formatted}` : formatted;
}

function addressLine(address = {}) {
  return [
    address.addressLine1,
    address.addressLine2,
    [address.city, address.stateOrProvince, address.postalCode].filter(Boolean).join(", "),
    address.countryCode,
  ]
    .filter(Boolean)
    .join(" ");
}

function trackingFromFulfillmentHref(hrefs = []) {
  const href = Array.isArray(hrefs) ? hrefs[0] : null;
  if (!href) return null;

  const parts = href.split("/").filter(Boolean);
  return parts[parts.length - 1] || null;
}

function ebayStatusTone(status) {
  const value = String(status || "").toLowerCase();

  if (["fulfilled", "paid", "complete", "completed"].some((item) => value.includes(item))) {
    return "good";
  }

  if (["cancel", "refund", "failed", "rejected"].some((item) => value.includes(item))) {
    return "bad";
  }

  if (["pending", "processing", "unfulfilled"].some((item) => value.includes(item))) {
    return "warn";
  }

  return "info";
}

function normalizeOrder(order = {}) {
  const lineItems = Array.isArray(order?.lineItems) ? order.lineItems : [];
  const buyerRegistrationAddress = order?.buyer?.buyerRegistrationAddress || {};
  const buyerContactAddress = buyerRegistrationAddress?.contactAddress || {};
  const shipInstruction =
    (Array.isArray(order?.fulfillmentStartInstructions)
      ? order.fulfillmentStartInstructions.find(
          (instruction) => instruction?.fulfillmentInstructionsType === "SHIP_TO"
        ) || order.fulfillmentStartInstructions[0]
      : null) || {};
  const shippingStep = shipInstruction?.shippingStep || {};
  const shipTo = shippingStep?.shipTo || {};
  const shipToAddress = shipTo?.contactAddress || buyerContactAddress || order?.buyer?.taxAddress || {};
  const firstLineItem = lineItems[0] || {};

  const currency = firstAvailable(
    order?.pricingSummary?.total?.currency,
    order?.paymentSummary?.totalDueSeller?.currency,
    order?.total?.currency,
    order?.orderTotal?.currency,
    "USD"
  );

  const taxTotal = lineItems.reduce((sum, item) => {
    const taxes = [
      ...(Array.isArray(item?.ebayCollectAndRemitTaxes) ? item.ebayCollectAndRemitTaxes : []),
      ...(Array.isArray(item?.taxes) ? item.taxes : []),
    ];

    return (
      sum +
      taxes.reduce(
        (taxSum, tax) =>
          taxSum + Number(firstAvailable(tax?.amount?.value, tax?.taxAmount?.value, 0) || 0),
        0
      )
    );
  }, 0);

  return {
    id: firstAvailable(order?.orderId, order?.legacyOrderId, order?.id) || "N/A",
    legacyOrderId: firstAvailable(order?.legacyOrderId, order?.orderId, order?.id) || "N/A",
    salesRecordReference: firstAvailable(order?.salesRecordReference, "N/A") || "N/A",
    sellerId: firstAvailable(order?.sellerId, "N/A") || "N/A",

    buyerName:
      firstAvailable(
        shipTo?.fullName,
        buyerRegistrationAddress?.fullName,
        order?.buyer?.username,
        "N/A"
      ) || "N/A",
    buyerUsername: firstAvailable(order?.buyer?.username, order?.buyer?.userName, "N/A") || "N/A",
    buyerEmail: firstAvailable(shipTo?.email, buyerRegistrationAddress?.email, "N/A") || "N/A",
    buyerPhone:
      firstAvailable(
        shipTo?.primaryPhone?.phoneNumber,
        buyerRegistrationAddress?.primaryPhone?.phoneNumber,
        ""
      ) || "",

    shipTo: {
      name: firstAvailable(shipTo?.fullName, buyerRegistrationAddress?.fullName, "N/A") || "N/A",
      address: addressLine(shipToAddress) || "N/A",
      city: shipToAddress?.city || "",
      state: shipToAddress?.stateOrProvince || "",
      postalCode: shipToAddress?.postalCode || "",
      country: shipToAddress?.countryCode || "",
    },

    itemCount: lineItems.reduce((sum, item) => sum + Number(item?.quantity || 0), 0),
    lineItems: lineItems.map((item) => ({
      id: firstAvailable(item?.lineItemId, item?.legacyItemId, item?.sku, item?.title) || "N/A",
      sku: firstAvailable(item?.sku, item?.legacyItemId, "N/A") || "N/A",
      title: firstAvailable(item?.title, item?.name, "Untitled eBay item") || "Untitled eBay item",
      quantity: Number(item?.quantity || 0),
      cost: moneyFromAmount(item?.lineItemCost, currency),
      total: moneyFromAmount(item?.total || item?.lineItemCost, currency),
      fulfillmentStatus:
        firstAvailable(item?.lineItemFulfillmentStatus, item?.fulfillmentStatus, "Pending") ||
        "Pending",
      shipByDate: firstAvailable(item?.lineItemFulfillmentInstructions?.shipByDate, null),
    })),

    subtotal: moneyFromAmount(order?.pricingSummary?.priceSubtotal, currency),
    deliveryCost: moneyFromAmount(order?.pricingSummary?.deliveryCost, currency),
    deliveryDiscount: formatSignedMoney(order?.pricingSummary?.deliveryDiscount, currency),
    total: moneyFromAmount(
      order?.pricingSummary?.total || order?.paymentSummary?.totalDueSeller || order?.total,
      currency
    ),
    dueSeller: moneyFromAmount(order?.paymentSummary?.totalDueSeller, currency),
    marketplaceFee: moneyFromAmount(order?.totalMarketplaceFee, currency),
    feeBasis: moneyFromAmount(order?.totalFeeBasisAmount, currency),
    taxTotal: taxTotal > 0 ? money(taxTotal, currency) : "N/A",

    fulfillmentStatus:
      firstAvailable(
        order?.orderFulfillmentStatus,
        order?.fulfillmentStatus,
        order?.status,
        "Pending"
      ) || "Pending",
    paymentStatus: firstAvailable(order?.orderPaymentStatus, order?.paymentStatus, "Pending") || "Pending",
    cancelState: firstAvailable(order?.cancelStatus?.cancelState, "NONE_REQUESTED") || "NONE_REQUESTED",

    carrier: firstAvailable(shippingStep?.shippingCarrierCode, "N/A") || "N/A",
    service: firstAvailable(shippingStep?.shippingServiceCode, "N/A") || "N/A",
    trackingNumber: trackingFromFulfillmentHref(order?.fulfillmentHrefs),
    minDeliveryDate: firstAvailable(shipInstruction?.minEstimatedDeliveryDate, null),
    maxDeliveryDate: firstAvailable(shipInstruction?.maxEstimatedDeliveryDate, null),
    shipByDate: firstAvailable(firstLineItem?.lineItemFulfillmentInstructions?.shipByDate, null),

    createdAt:
      firstAvailable(order?.creationDate, order?.createdAt, order?.orderDate, order?.date) || null,
    modifiedAt: firstAvailable(order?.lastModifiedDate, order?.updatedAt, null),
  };
}

function EmptyState({ title, message }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
      <p className="text-sm font-black text-gray-800">{title}</p>
      <p className="mt-2 text-sm leading-6 text-gray-500">{message}</p>
    </div>
  );
}

export default function Channelsyn() {
  const [selectedChannelName, setSelectedChannelName] = useState("ebay");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [ebayInventoryPage, setEbayInventoryPage] = useState(1);
  const [ebayInventoryPageSize, setEbayInventoryPageSize] = useState(10);
  const [ebayOrderPage, setEbayOrderPage] = useState(1);
  const [ebayOrderPageSize, setEbayOrderPageSize] = useState(10);
  const [ebayData, setEbayData] = useState(emptyEbayData);
  const [inventorySyncJob, setInventorySyncJob] = useState(null);

  const { dismiss, toast, toasts } = useToast();

  const loadChannels = useCallback(async () => {
    const data = await api.get("/channels");
    return mergeChannels(data.channels);
  }, []);

  const {
    data: channels,
    error,
    loading,
    reload,
    setData: setChannels,
  } = useAdminResource(loadChannels, mergeChannels([]));

  const selectedChannel =
    channels.find((channel) => channel.name === selectedChannelName) || channels[0];

  const visibleChannels = useMemo(() => {
    return channels.filter((channel) => {
      const statusMatches = statusFilter === "all" || channel.status === statusFilter;
      const searchMatches = [channel.name, channel.displayName, channel.status]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase());

      return statusMatches && searchMatches;
    });
  }, [channels, search, statusFilter]);

  const channelPagination = usePagination(visibleChannels, [search, statusFilter], 10);

  const ebayListings = useMemo(() => {
    return ebayData.inventory.items.map(normalizeInventoryItem);
  }, [ebayData.inventory.items]);

  const ebayInventoryTotal = Number(ebayData.inventory.total || ebayListings.length || 0);
  const ebayInventoryTotalPages = Math.max(
    1,
    Math.ceil(ebayInventoryTotal / ebayInventoryPageSize)
  );

  const ebayInventoryPagination = useMemo(
    () => ({
      page: ebayInventoryPage,
      pageSize: ebayInventoryPageSize,
      paginatedItems: ebayListings,
      setPage: setEbayInventoryPage,
      setPageSize: (nextPageSize) => {
        setEbayInventoryPageSize(Number(nextPageSize));
        setEbayInventoryPage(1);
      },
      totalItems: ebayInventoryTotal,
      totalPages: ebayInventoryTotalPages,
    }),
    [
      ebayInventoryPage,
      ebayInventoryPageSize,
      ebayInventoryTotal,
      ebayInventoryTotalPages,
      ebayListings,
    ]
  );

  const ebayOrders = useMemo(() => {
    return ebayData.orders.items.map(normalizeOrder);
  }, [ebayData.orders.items]);

  const ebayOrderTotal = Number(ebayData.orders.total || ebayOrders.length || 0);
  const ebayOrderTotalPages = Math.max(1, Math.ceil(ebayOrderTotal / ebayOrderPageSize));

  const ebayOrderPagination = useMemo(
    () => ({
      page: ebayOrderPage,
      pageSize: ebayOrderPageSize,
      paginatedItems: ebayOrders,
      setPage: setEbayOrderPage,
      setPageSize: (nextPageSize) => {
        setEbayOrderPageSize(Number(nextPageSize));
        setEbayOrderPage(1);
      },
      totalItems: ebayOrderTotal,
      totalPages: ebayOrderTotalPages,
    }),
    [ebayOrderPage, ebayOrderPageSize, ebayOrderTotal, ebayOrderTotalPages, ebayOrders]
  );

  useEffect(() => {
    setEbayOrderPage((currentPage) => Math.min(currentPage, ebayOrderTotalPages));
  }, [ebayOrderTotalPages]);

  useEffect(() => {
    setEbayInventoryPage((currentPage) => Math.min(currentPage, ebayInventoryTotalPages));
  }, [ebayInventoryTotalPages]);

  useEffect(() => {
    if (error && !loading) {
      toast(`Using preview channels: ${error}`, "warning");
    }
  }, [error, loading, toast]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    if (query.get("ebay_connected") === "0" || query.get("ebay_error")) {
      toast(
        query.get("ebay_error") || "Unable to connect eBay. Please try again.",
        "error"
      );
      window.history.replaceState(null, "", window.location.pathname);
      reload();
      return;
    }

    if (query.get("ebay_connected") === "1") {
      toast(
        query.get("ebay_already_connected") === "1"
          ? "eBay account was already connected."
          : "eBay account connected successfully.",
        "success"
      );
      window.history.replaceState(null, "", window.location.pathname);
      reload();
    }
  }, [reload, toast]);

  const fetchEbayData = useCallback(async () => {
    try {
      setEbayData((current) => ({
        ...current,
        loading: true,
        error: null,
      }));

      const orderQuery = new URLSearchParams({
        limit: String(ebayOrderPageSize),
        offset: String((ebayOrderPage - 1) * ebayOrderPageSize),
      });
      const inventoryQuery = new URLSearchParams({
        limit: String(ebayInventoryPageSize),
        offset: String((ebayInventoryPage - 1) * ebayInventoryPageSize),
      });

      const [accountResponse, inventoryResponse, ordersResponse] = await Promise.all([
        api.get("/channels/ebay/account").catch((err) => {
          console.error("Unable to load eBay account:", err);
          return null;
        }),
        api.get(`/channels/ebay/inventory?${inventoryQuery.toString()}`).catch((err) => {
          console.error("Unable to load eBay inventory:", err);
          return null;
        }),
        api.get(`/channels/ebay/orders?${orderQuery.toString()}`).catch((err) => {
          console.error("Unable to load eBay orders:", err);
          return null;
        }),
      ]);

      const inventory = extractInventoryItems(inventoryResponse || {});
      const orders = extractOrders(ordersResponse || {});

      setEbayData({
        accountInfo: normalizeEbayAccountInfo(accountResponse || selectedChannel || {}, selectedChannel),
        inventory,
        orders,
        loading: false,
        error: null,
      });
    } catch (err) {
      setEbayData((current) => ({
        ...current,
        loading: false,
        error: err.message || "Unable to load eBay data",
      }));
    }
  }, [
    ebayInventoryPage,
    ebayInventoryPageSize,
    ebayOrderPage,
    ebayOrderPageSize,
    selectedChannel,
  ]);

  useEffect(() => {
    if (selectedChannel?.name === "ebay" && selectedChannel?.status === "connected") {
      fetchEbayData();
    }
  }, [selectedChannel?.name, selectedChannel?.status, fetchEbayData]);

  const metrics = useMemo(() => {
    const connected = channels.filter((channel) => channel.status === "connected").length;
    const listings = channels.reduce(
      (sum, channel) => sum + Number(channel.stats?.activeListings || 0),
      0
    );
    const sales = channels.reduce(
      (sum, channel) => sum + Number(channel.stats?.sales30d || 0),
      0
    );
    const failed = channels.reduce(
      (sum, channel) => sum + Number(channel.stats?.failedJobs || 0),
      0
    );

    return {
      connected,
      listings,
      sales,
      failed,
    };
  }, [channels]);

  const replaceChannel = (nextChannel) => {
    setChannels((current) =>
      current.map((channel) =>
        channel.name === nextChannel.name ? enrichChannel(nextChannel) : channel
      )
    );
  };

  const setStatus = async (channelName, status) => {
    if (UNAVAILABLE_CHANNELS.has(channelName)) {
      return toast("This marketplace is not available yet.", "info");
    }

    try {
      const data = await api.patch(`/channels/${channelName}/status`, { status });
      replaceChannel(data.channel);
      toast(`${data.channel.displayName} ${status}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const connectEbay = async () => {
    try {
      const data = await api.get("/channels/ebay/auth-url");
      window.location.href = data.authUrl;
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const monitorInventorySync = async (jobId) => {
    for (let attempt = 0; attempt < 900; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
      try {
        const data = await api.get(`/channels/ebay/inventory-sync/${jobId}`);
        setInventorySyncJob(data.job);
        if (["completed", "completed_with_errors", "failed"].includes(data.job.status)) {
          toast(data.job.message, data.job.status === "failed" ? "error" : data.job.failed ? "warning" : "success");
          await reload();
          await fetchEbayData();
          return;
        }
      } catch (err) {
        toast(err.message || "Could not monitor eBay inventory sync", "error");
        return;
      }
    }
    toast("eBay inventory sync is still running. Refresh this page to check its latest state.", "info");
  };

  const sync = async (channelName, type, syncAll = false) => {
    if (UNAVAILABLE_CHANNELS.has(channelName)) {
      return toast("This marketplace is not available yet.", "info");
    }

    try {
      const data = await api.post(`/channels/${channelName}/sync`, { type, syncAll });

      if (data.channel) replaceChannel(data.channel);

      toast(data.message || `${type} sync queued`, "success");

      if (channelName === "ebay" && ["inventory", "locations"].includes(type) && data.job?.id) {
        setInventorySyncJob(data.job);
        monitorInventorySync(data.job.id);
        return;
      }

      if (channelName === "ebay") {
        fetchEbayData();
      }
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const refreshPageData = async () => {
    reload();

    if (selectedChannel?.name === "ebay" && selectedChannel?.status === "connected") {
      fetchEbayData();
    }
  };

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">
            Marketplace Brain
          </p>
          <h1 className="text-3xl font-black text-gray-900">Channel Sync</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Connect and manage the eBay seller account, active listings, inventory sync,
            and recent marketplace orders. Facebook and Walmart are listed as unavailable
            until those connectors are added.
          </p>
        </div>

        <WorkflowToolbar
          actions={[
            {
              label: "Connect eBay",
              primary: true,
              onClick: connectEbay,
            },
            {
              label: "Fetch eBay Products",
              onClick: () => sync("ebay", "products"),
            },
            {
              label: "Sync unsynced",
              onClick: () => sync("ebay", "inventory"),
            },
            {
              label: "Sync all eligible products",
              onClick: () => sync("ebay", "inventory", true),
            },
            {
              label: "Resync all listing locations",
              onClick: () => sync("ebay", "locations", true),
            },
            {
              label: "Refresh",
              onClick: refreshPageData,
            },
          ]}
        />
      </div>

      <p className="mb-5 text-sm text-gray-600">
        The listing count comes from live eBay listings. Product sync covers active, eBay-enabled local products with stock or an existing listing.
        Resync all listing locations applies the server-configured address to every active eBay listing, including listings outside the local product sync.
      </p>

      {inventorySyncJob && (
        <section className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 p-5 shadow-sm" aria-live="polite">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-blue-700">{inventorySyncJob.type === "locations" ? "eBay listing location synchronization" : "eBay inventory synchronization"}</p>
              <p className="mt-1 text-sm font-bold text-gray-900">{inventorySyncJob.message}</p>
              <p className="mt-1 text-xs font-semibold text-gray-600">
                {inventorySyncJob.processed || 0} processed | {inventorySyncJob.synced || 0} synced | {inventorySyncJob.failed || 0} failed
              </p>
              {inventorySyncJob.currentSku && <p className="mt-1 text-xs font-semibold text-gray-600">Current SKU: {inventorySyncJob.currentSku}</p>}
            </div>
            <StatusPill tone={inventorySyncJob.failed ? "warn" : ["completed"].includes(inventorySyncJob.status) ? "good" : "info"}>
              {inventorySyncJob.processed || 0}/{inventorySyncJob.total || 0} processed
            </StatusPill>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-white">
            <div
              className="h-full rounded-full bg-[#4391d1] transition-all"
              style={{ width: `${inventorySyncJob.total ? Math.min(100, (inventorySyncJob.processed / inventorySyncJob.total) * 100) : 0}%` }}
            />
          </div>
          {inventorySyncJob.errors?.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-bold text-amber-700">
                Show failure reasons ({inventorySyncJob.errors.length}
                {inventorySyncJob.failed > inventorySyncJob.errors.length ? ` of ${inventorySyncJob.failed}` : ""})
              </summary>
              <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-xl bg-white p-3 text-xs text-gray-700">
                {inventorySyncJob.errors.map((item, index) => (
                  <li key={`${item.sku}-${index}`}>
                    <span className="font-bold">{item.sku}</span>: {item.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <InsightCard
          title="Available Channels"
          value={`${metrics.connected}/${channels.length}`}
          note="Only eBay is actionable right now"
        />
        <InsightCard
          title="eBay Listings"
          value={metrics.listings}
          note="Listings tracked from eBay"
        />
        <InsightCard
          title="eBay Sales"
          value={money(metrics.sales)}
          note="eBay marketplace analytics"
          tone="money"
        />
        <InsightCard
          title="Failed Sync Jobs"
          value={metrics.failed}
          note="Errors appear with retry context"
          tone={metrics.failed ? "warn" : "good"}
        />
      </div>

      <section className="mb-6 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-black text-gray-900">Marketplace Connections</h2>
            <p className="mt-1 text-xs font-semibold text-gray-400">
              eBay can be connected now. Facebook and Walmart are intentionally locked until
              their connector work is ready.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-bold"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter channels"
              value={search}
            />

            <select
              className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-bold"
              onChange={(event) => setStatusFilter(event.target.value)}
              value={statusFilter}
            >
              <option value="all">All Status</option>
              <option value="connected">Connected</option>
              <option value="disconnected">Disconnected</option>
              <option value="syncing">Syncing</option>
              <option value="error">Error</option>
              <option value="not_available">Not available</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
              <tr>
                {[
                  "Channel",
                  "Status",
                  "Real-time",
                  "Listings",
                  "Orders 30d",
                  "Sales 30d",
                  "Last Sync",
                  "Actions",
                ].map((column) => (
                  <th className="whitespace-nowrap px-6 py-4 font-black" key={column}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {channelPagination.paginatedItems.map((channel) => {
                const recordStats = getChannelRecordStats(channel, ebayData);

                return (
                  <tr
                    className={`cursor-pointer text-sm text-gray-600 ${
                      selectedChannel?.name === channel.name ? "bg-blue-50/50" : ""
                    }`}
                    key={channel.name}
                    onClick={() => setSelectedChannelName(channel.name)}
                  >
                    <td className="px-6 py-4 font-black text-gray-900">
                      {channel.displayName}
                    </td>

                    <td className="px-6 py-4">
                      <StatusPill tone={statusTone(channel.status)}>
                        {channel.status === "not_available" ? "Not available" : channel.status}
                      </StatusPill>
                    </td>

                    <td className="px-6 py-4">
                      <StatusPill tone={channel.realTimeSync ? "good" : "neutral"}>
                        {channel.unavailable ? "N/A" : channel.realTimeSync ? "Enabled" : "Disabled"}
                      </StatusPill>
                    </td>

                    <td className="px-6 py-4 font-black text-gray-900">
                      {channel.unavailable ? "N/A" : number(recordStats.listings)}
                    </td>

                    <td className="px-6 py-4 font-black text-gray-900">
                      {channel.unavailable ? "N/A" : number(recordStats.orders30d)}
                    </td>

                    <td className="px-6 py-4 font-black text-gray-900">
                      {channel.unavailable ? "N/A" : money(recordStats.sales30d)}
                    </td>

                    <td className="px-6 py-4 text-xs font-bold text-gray-500">
                      {getLastSyncDate(channel) ? formatDateTime(getLastSyncDate(channel)) : "N/A"}
                    </td>

                    <td className="px-6 py-4">
                    {channel.unavailable ? (
                      <span className="inline-flex rounded-full bg-gray-100 px-3 py-2 text-xs font-black text-gray-500">
                        Not available
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                      <button
                        className="rounded-full bg-blue-50 px-3 py-2 text-xs font-black text-[#4391d1]"
                        onClick={(event) => {
                          event.stopPropagation();
                          sync(channel.name, "products");
                        }}
                        type="button"
                      >
                        Fetch Products
                      </button>

                      <button
                        className="rounded-full bg-gray-950 px-3 py-2 text-xs font-black text-white"
                        onClick={(event) => {
                          event.stopPropagation();
                          sync(channel.name, "inventory");
                        }}
                        type="button"
                      >
                        Sync unsynced
                      </button>

                      <button
                        className="rounded-full border border-gray-200 px-3 py-2 text-xs font-black text-gray-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          sync(channel.name, "inventory", true);
                        }}
                        type="button"
                      >
                        Sync all eligible products
                      </button>

                      <button
                        className="rounded-full border border-gray-200 px-3 py-2 text-xs font-black text-gray-700"
                        onClick={(event) => {
                          event.stopPropagation();

                          if (channel.status !== "connected") {
                            connectEbay();
                          } else {
                            setStatus(channel.name, "disconnected");
                          }
                        }}
                        type="button"
                      >
                        {channel.status === "connected" ? "Disconnect" : "Connect eBay"}
                      </button>
                      </div>
                    )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <PaginationControls pagination={channelPagination} />
      </section>

      <div className="grid gap-5">
        <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="grid gap-5 md:grid-cols-3">
            <div>
              <RefreshCw className="h-6 w-6 text-[#4391d1]" />
              <h2 className="mt-4 text-sm font-black uppercase text-gray-900">
                Initial Product Fetch
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-500">
                After eBay OAuth is connected, existing marketplace products import into
                admin for review and mapping.
              </p>
            </div>

            <div>
              <CloudUpload className="h-6 w-6 text-[#4391d1]" />
              <h2 className="mt-4 text-sm font-black uppercase text-gray-900">
                Admin Product Upload
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-500">
                Products created in admin can publish to eBay after required marketplace
                fields validate.
              </p>
            </div>

            <div>
              <BarChart3 className="h-6 w-6 text-[#4391d1]" />
              <h2 className="mt-4 text-sm font-black uppercase text-gray-900">
                Sales Analytics
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-500">
                Sales, orders, active listings, and failed jobs feed reports and the
                dashboard overview.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 shadow-sm">
          <div className="flex gap-3">
            <ShieldCheck className="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-700" />
            <p className="text-sm font-semibold leading-6 text-emerald-900">
              eBay OAuth is live and eBay sync is functional today. Credentials remain
              encrypted server-side.
            </p>
          </div>
        </section>
      </div>

      {selectedChannel?.name === "ebay" && selectedChannel?.status === "connected" && (
        <div className="mt-8 space-y-6">
          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-gray-100 bg-gradient-to-r from-gray-950 to-gray-800 px-6 py-6 text-white md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-white/10 p-3">
                  <User className="h-6 w-6" />
                </div>

                <div>
                  <h2 className="text-xl font-black">eBay Account Information</h2>
                  <p className="mt-1 text-sm font-semibold text-white/70">
                    Real connected seller account details from your eBay integration.
                  </p>
                </div>
              </div>

              <button
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-black text-gray-950 hover:bg-gray-100"
                onClick={fetchEbayData}
                type="button"
              >
                <RefreshCw className={`h-4 w-4 ${ebayData.loading ? "animate-spin" : ""}`} />
                Refresh eBay Data
              </button>
            </div>

            <div className="p-6">
              {ebayData.loading && !ebayData.accountInfo ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-6 text-sm font-bold text-gray-500">
                  Loading eBay account information...
                </div>
              ) : ebayData.accountInfo ? (
                <>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-400">
                        Seller ID
                      </p>
                      <p className="mt-2 break-all text-lg font-black text-gray-900">
                        {ebayData.accountInfo.sellerId}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-400">
                        Seller Name
                      </p>
                      <p className="mt-2 text-lg font-black text-gray-900">
                        {ebayData.accountInfo.sellerName}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-400">
                        Marketplace
                      </p>
                      <p className="mt-2 text-lg font-black text-gray-900">
                        {ebayData.accountInfo.marketplace}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-400">
                        Status
                      </p>
                      <p className="mt-2 text-lg font-black capitalize text-emerald-600">
                        {ebayData.accountInfo.accountStatus}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-4 md:grid-cols-3">
                    <div className="rounded-2xl border border-gray-100 bg-white p-5">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-400">
                        Real-Time Sync
                      </p>
                      <p className="mt-2 text-sm font-black text-gray-900">
                        {ebayData.accountInfo.realTimeSync ? "Enabled" : "Disabled"}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-white p-5">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-400">
                        Last Product Sync
                      </p>
                      <p className="mt-2 text-sm font-black text-gray-900">
                        {formatDateTime(ebayData.accountInfo.lastProductSyncAt)}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-white p-5">
                      <p className="text-xs font-black uppercase tracking-wide text-gray-400">
                        Last Inventory Sync
                      </p>
                      <p className="mt-2 text-sm font-black text-gray-900">
                        {formatDateTime(ebayData.accountInfo.lastInventorySyncAt)}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState
                  title="No eBay account details found"
                  message="Your account is connected, but the backend did not return seller profile details yet."
                />
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-blue-50 p-3">
                  <Package className="h-5 w-5 text-[#4391d1]" />
                </div>

                <div>
                  <h2 className="text-lg font-black text-gray-900">Active eBay Listings</h2>
                  <p className="mt-1 text-xs font-semibold text-gray-400">
                    Live inventory and listing records returned by eBay.
                  </p>
                </div>
              </div>

              <div className="rounded-full bg-gray-50 px-4 py-2 text-xs font-black text-gray-500">
                {number(ebayData.inventory.total)} total listings
              </div>
            </div>

            <div className="p-6">
              {ebayData.loading && ebayListings.length === 0 ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-6 text-sm font-bold text-gray-500">
                  Loading active eBay listings...
                </div>
              ) : ebayListings.length > 0 ? (
                <div>
                  {ebayData.loading && (
                    <div className="mb-4 flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-black text-blue-700">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Loading eBay inventory...
                    </div>
                  )}

                  <div className="relative overflow-x-auto">
                    {ebayData.loading && (
                      <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/70 pt-8 backdrop-blur-[1px]">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-black text-gray-700 shadow-sm ring-1 ring-gray-100">
                          <RefreshCw className="h-4 w-4 animate-spin text-[#4391d1]" />
                          Updating inventory
                        </div>
                      </div>
                    )}

                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                        <tr>
                          <th className="whitespace-nowrap px-4 py-3 font-black">Listing</th>
                          <th className="whitespace-nowrap px-4 py-3 font-black">Quantity</th>
                          <th className="whitespace-nowrap px-4 py-3 font-black">Price</th>
                          <th className="whitespace-nowrap px-4 py-3 font-black">Condition</th>
                          <th className="whitespace-nowrap px-4 py-3 font-black">Status</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-100">
                        {ebayInventoryPagination.paginatedItems.map((item) => (
                          <tr className="text-gray-600" key={`${item.id}-${item.sku}`}>
                            <td className="min-w-[360px] px-4 py-4">
                              <div className="flex items-center gap-4">
                                {item.image ? (
                                  <img
                                    alt={item.title}
                                    className="h-16 w-16 flex-shrink-0 rounded-xl border border-gray-100 bg-white object-contain"
                                    loading="lazy"
                                    src={item.image}
                                  />
                                ) : (
                                  <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 text-xs font-black text-gray-300">
                                    IMG
                                  </div>
                                )}

                                <div>
                                  <p className="font-black text-gray-900">{item.sku}</p>
                                  <p className="mt-1 font-bold text-gray-900">{item.title}</p>
                                  <p className="mt-1 text-xs text-gray-400">ID: {item.id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 font-bold">{number(item.quantity)}</td>
                            <td className="px-4 py-4 font-black text-gray-900">{item.price}</td>
                            <td className="px-4 py-4">{item.condition}</td>
                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${
                                  item.status.toLowerCase().includes("out")
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <PaginationControls pagination={ebayInventoryPagination} />
                </div>
              ) : (
                <EmptyState
                  title="No active eBay listings found"
                  message="No inventory/listing records were returned. Run Fetch eBay Products or confirm your backend is returning inventoryItems, listings, offers, or activeListings."
                />
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-emerald-50 p-3">
                  <ShoppingCart className="h-5 w-5 text-emerald-600" />
                </div>

                <div>
                  <h2 className="text-lg font-black text-gray-900">Recent eBay Orders</h2>
                  <p className="mt-1 text-xs font-semibold text-gray-400">
                    Latest customer orders from your eBay seller account.
                  </p>
                </div>
              </div>

              <div className="rounded-full bg-gray-50 px-4 py-2 text-xs font-black text-gray-500">
                {number(ebayData.orders.total)} total orders
              </div>
            </div>

            <div className="p-6">
              {ebayData.loading && ebayOrders.length === 0 ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-6 text-sm font-bold text-gray-500">
                  Loading recent eBay orders...
                </div>
              ) : ebayOrders.length > 0 ? (
                <div>
                  {ebayData.loading && (
                    <div className="mb-4 flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-black text-blue-700">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Loading eBay orders...
                    </div>
                  )}

                  <div className="relative overflow-x-auto">
                    {ebayData.loading && (
                      <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/70 pt-8 backdrop-blur-[1px]">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-black text-gray-700 shadow-sm ring-1 ring-gray-100">
                          <RefreshCw className="h-4 w-4 animate-spin text-[#4391d1]" />
                          Updating page
                        </div>
                      </div>
                    )}

                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                        <tr>
                          <th className="min-w-[190px] px-4 py-3 font-black">Order</th>
                          <th className="min-w-[260px] px-4 py-3 font-black">Buyer / Ship To</th>
                          <th className="min-w-[310px] px-4 py-3 font-black">Items</th>
                          <th className="min-w-[220px] px-4 py-3 font-black">Payment</th>
                          <th className="min-w-[250px] px-4 py-3 font-black">Shipping</th>
                          <th className="min-w-[180px] px-4 py-3 font-black">Dates</th>
                          <th className="min-w-[160px] px-4 py-3 font-black">Status</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-100">
                        {ebayOrderPagination.paginatedItems.map((order) => (
                          <tr className="align-top text-gray-600" key={order.id}>
                            <td className="px-4 py-4">
                              <p className="font-black text-gray-900">{order.id}</p>
                              <p className="mt-1 text-xs font-semibold text-gray-400">
                                Legacy: {order.legacyOrderId}
                              </p>
                              <p className="mt-1 text-xs font-semibold text-gray-400">
                                Sales record: {order.salesRecordReference}
                              </p>
                              <p className="mt-1 text-xs font-semibold text-gray-400">
                                Seller: {order.sellerId}
                              </p>
                            </td>

                            <td className="px-4 py-4">
                              <p className="font-black text-gray-900">{order.buyerName}</p>
                              <p className="mt-1 text-xs font-semibold text-gray-500">
                                @{order.buyerUsername}
                              </p>
                              <p className="mt-2 break-all text-xs font-semibold text-gray-500">
                                {order.buyerEmail}
                              </p>
                              {order.buyerPhone && (
                                <p className="mt-1 text-xs font-semibold text-gray-500">
                                  {order.buyerPhone}
                                </p>
                              )}
                              <p className="mt-3 text-xs leading-5 text-gray-500">
                                <span className="font-black text-gray-700">
                                  {order.shipTo.name}
                                </span>
                                <br />
                                {order.shipTo.address}
                              </p>
                            </td>

                            <td className="px-4 py-4">
                              <div className="space-y-3">
                                {order.lineItems.map((item) => (
                                  <div
                                    className="rounded-xl border border-gray-100 bg-gray-50 p-3"
                                    key={item.id}
                                  >
                                    <p className="font-black text-gray-900">{item.title}</p>
                                    <p className="mt-1 text-xs font-semibold text-gray-500">
                                      SKU: {item.sku} | Qty: {number(item.quantity)} | {item.total}
                                    </p>
                                    <p className="mt-1 text-xs font-semibold text-gray-400">
                                      Line status: {item.fulfillmentStatus}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </td>

                            <td className="px-4 py-4">
                              <div className="space-y-1 text-xs font-semibold text-gray-500">
                                <p>
                                  Total:{" "}
                                  <span className="font-black text-gray-900">{order.total}</span>
                                </p>
                                <p>Subtotal: {order.subtotal}</p>
                                <p>Shipping: {order.deliveryCost}</p>
                                <p>Discount: {order.deliveryDiscount}</p>
                                <p>Tax collected: {order.taxTotal}</p>
                                <p>Due seller: {order.dueSeller}</p>
                                <p>Marketplace fee: {order.marketplaceFee}</p>
                              </div>
                            </td>

                            <td className="px-4 py-4">
                              <p className="font-black text-gray-900">
                                {order.carrier} / {order.service}
                              </p>
                              <p className="mt-2 text-xs font-semibold text-gray-500">
                                Tracking: {order.trackingNumber || "Not provided"}
                              </p>
                              <p className="mt-2 text-xs font-semibold text-gray-500">
                                ETA: {formatDate(order.minDeliveryDate)} -{" "}
                                {formatDate(order.maxDeliveryDate)}
                              </p>
                            </td>

                            <td className="px-4 py-4 text-xs font-semibold text-gray-500">
                              <p>
                                Created:{" "}
                                <span className="font-black text-gray-800">
                                  {formatDateTime(order.createdAt)}
                                </span>
                              </p>
                              <p className="mt-2">
                                Modified: {formatDateTime(order.modifiedAt)}
                              </p>
                              <p className="mt-2">Ship by: {formatDateTime(order.shipByDate)}</p>
                            </td>

                            <td className="px-4 py-4">
                              <div className="space-y-2">
                                <StatusPill tone={ebayStatusTone(order.paymentStatus)}>
                                  {order.paymentStatus}
                                </StatusPill>
                                <StatusPill tone={ebayStatusTone(order.fulfillmentStatus)}>
                                  {order.fulfillmentStatus}
                                </StatusPill>
                                {order.cancelState !== "NONE_REQUESTED" && (
                                  <StatusPill tone="bad">{order.cancelState}</StatusPill>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <PaginationControls pagination={ebayOrderPagination} />
                </div>
              ) : (
                <EmptyState
                  title="No recent eBay orders found"
                  message="No order records were returned. Confirm your backend is returning eBay orders from the fulfillment API."
                />
              )}
            </div>
          </section>

          {ebayData.error && (
            <section className="rounded-2xl border border-red-200 bg-red-50 p-6">
              <div className="flex gap-4">
                <AlertCircle className="mt-1 h-5 w-5 flex-shrink-0 text-red-600" />
                <div>
                  <h3 className="font-black text-red-900">Unable to load eBay data</h3>
                  <p className="mt-1 text-sm text-red-800">{ebayData.error}</p>
                </div>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
